import { cn } from '@app/utils/cn';
import { handleEscapeKey } from '@app/utils/handleEscapeKey';
import { useBodyLock } from '@app/utils/hooks/useBodyLock';
import cx from 'classnames';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Button } from '../Button';
import { FocusTrap } from '../FocusTrap';
import { CancelSmall, ChevronDown } from '../Icons';

interface Props {
  title: React.ReactNode;
  isOpen: boolean;
  isHidden?: boolean;
  isNested?: boolean;
  onBack?: () => void;
  onClose: () => void;
  className?: string;
}

export const DropdownDrawer = ({
  title,
  children,
  isOpen,
  isHidden = false,
  isNested = false,
  onBack,
  onClose,
  className,
}: React.PropsWithChildren<Props>) => {
  const [isExited, setIsExited] = React.useState(isOpen);

  useBodyLock(isOpen);

  return (
    <CSSTransition
      timeout={{
        enter: 250,
        exit: 150,
      }}
      in={isOpen && !isHidden}
      classNames={{
        enter: 'opacity-0 translate-y-[10px]',
        enterActive: '!opacity-100 !translate-y-0 transition-[opacity,transform] delay-100',
        exit: 'opacity-100',
        exitActive: '!opacity-0 transition-[opacity,transform]',
      }}
      onEnter={() => setIsExited(false)}
      onExited={() => setIsExited(true)}
      unmountOnExit={!isOpen}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onKeyDown={handleEscapeKey(isNested ? onBack : onClose)}
        className={cx(
          'pointer-events-none fixed bottom-0 left-0 right-0 top-16 z-drawer flex w-full flex-col justify-end overflow-hidden',
          {
            hidden: isHidden && isExited,
          },
        )}
      >
        <FocusTrap>
          <div className={cn('pointer-events-auto flex flex-col overflow-hidden rounded-t-regular bg-white px-4 py-5', className)}>
            <div className="mb-5 flex justify-between">
              <div className="flex gap-1">
                {isNested && (
                  <button type="button" className="-m-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full" onClick={onBack}>
                    <ChevronDown className="rotate-90" />
                  </button>
                )}
                <h3 data-sentry-mask className="line-clamp-2 heading-3">
                  {title}
                </h3>
              </div>
              <Button icon={CancelSmall} size="medium" variant="ghost" color="secondary" className="-mt-1" onClick={onClose} />
            </div>
            <div className="overflow-auto">{children}</div>
          </div>
        </FocusTrap>
      </div>
    </CSSTransition>
  );
};
